// S11 AI reports: the URL's filters (search params) turned into the oversight API's filter
// (M9: `from` inclusive, `to` exclusive; no period means the last 7 days).
import type { OversightFilter } from '@feedback/shared/contracts/oversight';

export const PERIODS = ['7', '30', 'custom'] as const;
export type Period = (typeof PERIODS)[number];

export interface ReportSearch {
  appId?: string;
  environmentId?: string;
  period?: Period;
  /** Custom range, YYYY-MM-DD, both days included. */
  from?: string;
  to?: string;
  page?: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export function parseReportSearch(search: Record<string, unknown>): ReportSearch {
  const period = PERIODS.find((p) => p === String(search.period ?? ''));
  const from = str(search.from);
  const to = str(search.to);
  const page = Number(search.page);
  return {
    ...(str(search.appId) ? { appId: str(search.appId) } : {}),
    ...(str(search.environmentId) ? { environmentId: str(search.environmentId) } : {}),
    ...(period ? { period } : {}),
    ...(from && DATE.test(from) ? { from } : {}),
    ...(to && DATE.test(to) ? { to } : {}),
    ...(Number.isInteger(page) && page > 1 ? { page } : {}),
  };
}

function nextDay(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  return new Date(d.getTime() + DAY_MS).toISOString().slice(0, 10);
}

/**
 * The period as the API's from (inclusive) and to (exclusive), as whole UTC days so the query
 * keys stay the same all day: the last N days up to and including today.
 */
export function periodRange(search: ReportSearch, now: number = Date.now()): { from: string; to: string } {
  if (search.period === 'custom' && search.from && search.to) {
    return { from: search.from, to: nextDay(search.to) };
  }
  const days = search.period === '30' ? 30 : 7;
  const today = new Date(now).toISOString().slice(0, 10);
  const from = new Date(now - (days - 1) * DAY_MS).toISOString().slice(0, 10);
  return { from, to: nextDay(today) };
}

/** The filter for the list, summary and export calls (no period: the server's last 7 days). */
export function reportFilter(search: ReportSearch): OversightFilter {
  const explicit = search.period === '30' || (search.period === 'custom' && search.from && search.to);
  return {
    ...(search.appId ? { appId: search.appId } : {}),
    ...(search.environmentId ? { environmentId: search.environmentId } : {}),
    ...(explicit ? periodRange(search) : {}),
  };
}

export function percent(value: number | null): string {
  return value === null ? '–' : `${Math.round(value * 100)}%`;
}
